import * as moment from 'moment';

// Shared by isoWeek()/isoYear() below — parses a date-only string or Date as
// a local calendar day (avoiding UTC-midnight shifting the day), then
// adjusts it to the Thursday of its ISO-8601 week, per the standard
// "the ISO week/year is the week/year of its Thursday" rule. Kept as one
// function so a correctness fix only ever needs to be made once.
function isoThursday(value: string | Date): Date | null {

    let match = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
    let date = match
        ? new Date(+match[1], +match[2] - 1, +match[3])
        : value instanceof Date
            ? new Date(value.getFullYear(), value.getMonth(), value.getDate())
            : new Date(value);

    if (isNaN(date.getTime())) return null;

    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));

    return date;

}

// ISO-8601 week number — pinned so week labels agree with the API's PHP formatter (Culteva\Services\SampleImageLabelFormatter)
export function isoWeek(value: string | Date): number | null {

    let date = isoThursday(value);
    if (!date) return null;

    let week1 = new Date(date.getFullYear(), 0, 4);

    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);

}

// The ISO-8601 week-numbering year for a given date — can differ from the
// calendar year near year boundaries (e.g. Dec 31 can fall in ISO week 1 of
// the following year). Paired with isoWeek() above; shares its Thursday-of-week logic.
export function isoYear(value: string | Date): number | null {

    let date = isoThursday(value);
    return date ? date.getFullYear() : null;

}

export function parseDuration(value: string): { time: number, unit: 'd' | 'w' | 'm' } | null {

    if (typeof value === 'string') {
        let matches = value.match(/^([0-9]+)([dwm])$/);
        if (matches && matches.length === 3) {
            return { time: parseInt(matches[1]), unit: <'d' | 'w' | 'm'>matches[2] };
        }
    }

    return null;

}

// Moment's lowercase 'm' means minutes, not months — map to 'M' so month durations add months. Never mutates `start`.
export function addDuration(start: moment.Moment, duration: { time: number, unit: 'd' | 'w' | 'm' } | null): moment.Moment {

    if (!duration) return start.clone();

    const momentUnit: moment.unitOfTime.DurationConstructor = duration.unit === 'm' ? 'M' : duration.unit;

    return start.clone().add(duration.time, momentUnit);

}

